import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';

import { AccessDenied } from '../../components/access-denied';
import { t } from '../../lib/i18n';
import { useSessionRoles } from '../auth/use-session-roles';
import { type RelayQueueItem } from '../bids/types';
import { RELAY_QUEUE_QUERY_KEY } from '../bids/hooks/use-bid-mutations';
import { useMirrorBid } from '../bids/hooks/use-mirror-bid';
import { type AuctionScheduleItem } from '../auction-schedule/types';
import { type DashboardMetric, useDashboardMetrics } from './use-dashboard-metrics';

/**
 * Overview — `Redesign-D-Admin` / `Redesign-D-Admin-Mobile`, screen «Огляд».
 *
 * The artboard's composition is an argument, not decoration: seven counts of
 * what is waiting, then the relay queue in full, because a captured bid nobody
 * mirrors is a client silently losing a lot. Desktop puts the queue beside
 * today's closings; mobile stacks them. Everything is live — the artboard's
 * numbers were mock, the queues are not.
 */
export function DashboardPage() {
  const { roles } = useSessionRoles();
  const { metrics, relay, today, isForbidden, isFetching, refetchAll } = useDashboardMetrics(
    t,
    roles,
  );

  if (isForbidden) return <AccessDenied />;

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('admin.dashboard.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            <span className="lg:hidden">{shortHeaderLine()}</span>
            <span className="hidden lg:inline">{longHeaderLine()}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={refetchAll}
          disabled={isFetching}
          className="hidden h-10 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink disabled:opacity-50 lg:inline-flex"
        >
          <RefreshCw size={16} strokeWidth={1.75} className={isFetching ? 'animate-spin' : ''} />
          {t('admin.dashboard.refresh')}
        </button>
      </div>

      {/* ── Tiles ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] lg:gap-3">
        {metrics.map((metric) => (
          <MetricTile key={metric.key} metric={metric} />
        ))}
      </div>

      {/* ── Relay queue + today ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[repeat(auto-fit,minmax(340px,1fr))] lg:items-start lg:gap-5">
        <RelaySection {...relay} />
        <TodaySection {...today} />
      </div>
    </>
  );
}

/** «Ср, 16 вересня · що потребує оператора» — the mobile header's second line. */
function shortHeaderLine(): string {
  return `${formatToday('short')} · ${t('admin.dashboard.needsOperatorShort')}`;
}

/** The desktop line adds the refresh clock the artboard prints after it. */
function longHeaderLine(): string {
  return [
    formatToday('long'),
    t('admin.dashboard.needsOperator'),
    t('admin.dashboard.updated', { time: formatClock(new Date()) }),
  ].join(' · ');
}

const TONE_RULE: Record<DashboardMetric['tone'], string> = {
  line: 'shadow-[inset_0_3px_0_var(--color-cf-line)]',
  danger: 'shadow-[inset_0_3px_0_var(--color-cf-danger)]',
  accent: 'shadow-[inset_0_3px_0_var(--color-cf-accent)]',
  info: 'shadow-[inset_0_3px_0_var(--color-cf-info)]',
};

function MetricTile({ metric }: { metric: DashboardMetric }) {
  const { key, to, tab, value, sub, subShort, tone, onMobile, isLoading, isError } = metric;

  return (
    <Link
      to={to}
      search={tab ? { tab } : undefined}
      className={`flex flex-col rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 hover:border-cf-brand lg:px-[18px] lg:py-4 ${TONE_RULE[tone]} ${
        onMobile ? '' : 'hidden lg:flex'
      }`}
    >
      <p className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase lg:tracking-[0.14em]">
        <span className="lg:hidden">{t(`admin.dashboard.tiles.${key}.labelShort`)}</span>
        <span className="hidden lg:inline">{t(`admin.dashboard.tiles.${key}.label`)}</span>
      </p>
      <p
        className={`mt-2 font-mono text-[24px] leading-none font-medium lg:mt-1 lg:text-[30px] ${
          isError ? 'text-cf-ink-faint' : tone === 'danger' ? 'text-cf-danger' : 'text-cf-ink'
        }`}
      >
        {isLoading ? '…' : isError ? '—' : value}
      </p>
      <p className="mt-1 text-[11px] text-cf-ink-subtle lg:text-xs">
        <span className="lg:hidden">{subShort}</span>
        <span className="hidden lg:inline">{sub}</span>
      </p>
    </Link>
  );
}

function RelaySection({
  rows,
  overdue,
  isLoading,
  isError,
}: {
  rows: RelayQueueItem[];
  overdue: number;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface lg:overflow-x-auto">
      <div className="flex items-center justify-between gap-2 border-b border-cf-line-soft px-3.5 py-3 lg:min-w-[520px] lg:px-[18px] lg:py-3.5">
        <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
          {t('admin.dashboard.relaySection.title')}
        </h2>
        {!isLoading && !isError && <SlaChip overdue={overdue} />}
      </div>

      {isLoading ? (
        <Placeholder text={t('common.loading.data')} />
      ) : isError ? (
        <Placeholder text={t('admin.dashboard.failed')} />
      ) : rows.length === 0 ? (
        <Placeholder text={t('admin.dashboard.relaySection.empty')} />
      ) : (
        rows.map((row) => <RelayRow key={row.bidId} row={row} />)
      )}

      {/*
        Desktop only, as the artboard has it: on mobile the bottom bar already
        carries «Ставки», and the row of links between the queue and «Сьогодні»
        pushed the day's closings off the first screen.
      */}
      <Link
        to="/deals"
        search={{ tab: 'relay' }}
        className="hidden px-3.5 py-3 text-[13px] font-semibold text-cf-brand lg:block lg:px-[18px]"
      >
        {t('admin.dashboard.relaySection.open')}
      </Link>
    </section>
  );
}

function SlaChip({ overdue }: { overdue: number }) {
  if (overdue > 0) {
    return (
      <span className="rounded-cf-sm bg-cf-danger-tint px-[7px] py-[3px] font-mono text-[10px] tracking-[0.08em] text-cf-danger uppercase lg:px-2 lg:py-1">
        {t('admin.dashboard.relaySection.slaOverdue', { count: overdue })}
      </span>
    );
  }
  return (
    <span className="rounded-cf-sm bg-cf-success-tint px-[7px] py-[3px] font-mono text-[10px] tracking-[0.08em] text-cf-success uppercase lg:px-2 lg:py-1">
      <span className="lg:hidden">{t('admin.dashboard.relaySection.slaOkShort')}</span>
      <span className="hidden lg:inline">{t('admin.dashboard.relaySection.slaOk')}</span>
    </span>
  );
}

export function RelayRow({ row }: { row: RelayQueueItem }) {
  const queryClient = useQueryClient();
  // «Обробити» (owner, 2026-10-26) — the same claim → confirm-relay flow as the
  // bid page's «Оброблено»; the old one-tap «Віддзеркалити» skipped the claim
  // (403 CLAIM_REQUIRED) and the «вища ставка» step.
  const mirror = useMirrorBid(() =>
    queryClient.invalidateQueries({ queryKey: RELAY_QUEUE_QUERY_KEY }),
  );
  const car = [row.car?.brand, row.car?.model].filter(Boolean).join(' ') || row.car?.listing_id || '—';
  const who = row.user?.email ?? '—';
  // The artboard's second line is «bidder · house · lot»; a raw Mongo id would
  // blow the column out, so the lot is shown the way the operator reads it out
  // loud at the auction — its last four characters.
  const lot = shortLot(row.car?.listing_id ?? row.carId);
  const amount = new Intl.NumberFormat('uk-UA').format(row.bid);
  const sla = slaLabel(row);
  // The artboard's three severities, in the ink the legend assigns them: red is
  // late, amber is close, and anything else is the ordinary subtle grey — a
  // green "on track" on every row would make the red ones harder to find.
  const slaTone =
    row.slaSeverity === 'red'
      ? 'text-cf-danger'
      : row.slaSeverity === 'amber'
        ? 'text-cf-ok'
        : 'text-cf-ink-subtle';

  return (
    <div className="flex items-center gap-2.5 border-b border-cf-line-soft px-3.5 py-2.5 lg:grid lg:min-w-[520px] lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:gap-3.5 lg:px-[18px] lg:py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold lg:text-sm">{car}</div>
        <div className="truncate text-[11px] text-cf-ink-subtle lg:text-xs">
          <span className="lg:hidden">
            {who} · CHF {amount} · <span className={slaTone}>{sla}</span>
          </span>
          <span className="hidden lg:inline">
            {who} · {lot}
          </span>
        </div>
      </div>

      <div className="hidden text-right lg:block">
        <div className="font-mono text-base font-medium">CHF {amount}</div>
        <div className={`font-mono text-[11px] ${slaTone}`}>{sla}</div>
      </div>

      <button
        type="button"
        onClick={() => void mirror.start(row.bidId, row.bid)}
        disabled={mirror.busy}
        data-testid="dashboard-relay-process"
        className="h-9 shrink-0 rounded-cf-md bg-cf-brand px-2.5 text-xs font-semibold whitespace-nowrap text-white disabled:opacity-50 lg:h-[34px] lg:px-3 lg:text-[13px]"
      >
        {t('admin.dashboard.relaySection.process')}
      </button>
      {mirror.dialog}
    </div>
  );
}

function TodaySection({
  rows,
  isLoading,
  isError,
}: {
  rows: AuctionScheduleItem[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
      <div className="border-b border-cf-line-soft px-3.5 py-3 lg:px-[18px] lg:py-3.5">
        <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
          {t('admin.dashboard.today.title')}
        </h2>
      </div>

      {isLoading ? (
        <Placeholder text={t('common.loading.data')} />
      ) : isError ? (
        <Placeholder text={t('admin.dashboard.failed')} />
      ) : rows.length === 0 ? (
        <Placeholder text={t('admin.dashboard.today.empty')} />
      ) : (
        rows.map((item) => (
          <div
            key={item._id}
            className="flex items-start gap-2.5 border-b border-cf-line-soft px-3.5 py-2.5 text-[13px] leading-[1.4] lg:gap-3 lg:px-[18px] lg:py-[11px]"
          >
            <span className="w-10 shrink-0 font-mono text-cf-ink-subtle lg:w-11">
              {formatClock(new Date(item.end_date))}
            </span>
            <span
              className={`mt-[5px] h-2 w-2 shrink-0 rounded-cf-xs ${
                item.status === 'ending' ? 'bg-cf-danger' : 'bg-cf-accent'
              }`}
            />
            <span className="min-w-0">
              {item.car_name}
              {item.bidCount ? (
                <span className="text-cf-ink-subtle">
                  {' · '}
                  {t('admin.dashboard.today.bids', { count: item.bidCount })}
                </span>
              ) : null}
            </span>
          </div>
        ))
      )}
    </section>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <p className="border-b border-cf-line-soft px-3.5 py-6 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]">
      {text}
    </p>
  );
}

/** «Ср, 16 вересня» (short) or «Середа, 16 вересня» (long), in the UI locale. */
function formatToday(width: 'short' | 'long'): string {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('uk-UA', {
    weekday: width === 'long' ? 'long' : 'short',
  }).format(now);
  const day = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'long' }).format(now);
  const capitalised = weekday.charAt(0).toUpperCase() + weekday.slice(1).replace(/\.$/, '');
  return `${capitalised}, ${day}`;
}

/**
 * «SLA 14 хв» — how long the operator still has before the real auction closes
 * this lot. The artboard prints a countdown rather than the pipeline state
 * because the state is already implied by the row being in this queue; what the
 * operator needs is whether they have time.
 */
function shortLot(id: string | null | undefined): string {
  if (!id) return '—';
  return id.replace(/[^0-9a-z]/gi, '').slice(-4).toUpperCase();
}

function slaLabel(row: RelayQueueItem): string {
  if (!row.tenderCloseAt) return t('admin.dashboard.relaySection.noDeadline');
  const msLeft = new Date(row.tenderCloseAt).getTime() - Date.now();
  if (Number.isNaN(msLeft)) return t('admin.dashboard.relaySection.noDeadline');
  if (msLeft <= 0) return t('admin.dashboard.relaySection.slaOverdueRow');

  const totalMinutes = Math.floor(msLeft / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const left =
    hours > 0
      ? t('admin.dashboard.relaySection.hours', { h: hours, m: minutes })
      : t('admin.dashboard.relaySection.minutes', { m: minutes });
  return t('admin.dashboard.relaySection.sla', { left });
}

function formatClock(date: Date): string {
  return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(date);
}
